import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { resetClientPassword } from '../services/emailService';
import { validatePassword } from '../utils/validators';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import stoneGoldBg from '../assets/images/stone-gold-black-bg.png';

function ClientResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const bgStyle = {
    backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.85), rgba(0,0,0,0.75)), url(${stoneGoldBg})`
  };

  // Missing or invalid link params
  if (!email || !token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
        style={bgStyle}
      >
        <div className="max-w-md w-full">
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-crimson" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Invalid Link</h1>
            <p className="text-neutral-grey-300 mb-6">
              This reset link is missing or invalid. Please request a new password reset from the client portal.
            </p>
            <Link
              to="/client/portal"
              className="inline-block px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
            >
              Go to Client Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};

    const passwordError = validatePassword(password);
    if (passwordError) errors.password = passwordError;

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    setSubmitError('');

    const result = await resetClientPassword({ email, token, password });

    if (result.success) {
      setIsSuccess(true);
    } else {
      setSubmitError(result.error || 'Failed to reset password. Please try again.');
    }

    setIsSubmitting(false);
  };

  // Success state
  if (isSuccess) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
        style={bgStyle}
      >
        <div className="max-w-md w-full">
          <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Password Reset!</h1>
            <p className="text-neutral-grey-300 mb-8">
              Your password has been updated successfully. You can now log in with your new password.
            </p>
            <Link
              to="/client/portal"
              className="inline-block px-8 py-3 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all shadow-lg shadow-gold/30"
            >
              Go to Client Portal
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Password reset form
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 bg-cover bg-center bg-fixed"
      style={bgStyle}
    >
      <div className="max-w-md w-full">
        <div className="bg-neutral-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-gold" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">
              Reset Your Password
            </h1>
            <p className="text-neutral-grey-300">
              Enter a new password for <span className="text-gold font-semibold">{email}</span>
            </p>
          </div>

          {submitError && (
            <div className="bg-crimson/10 border border-crimson text-crimson px-4 py-3 rounded-lg mb-6">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className="block text-white font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                id="newPassword"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormErrors(prev => ({ ...prev, password: null }));
                }}
                className={`w-full px-4 py-3 bg-white/10 border-2 ${
                  formErrors.password ? 'border-crimson' : 'border-white/20'
                } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                placeholder="Min. 6 characters"
                autoFocus
              />
              {formErrors.password && (
                <p className="mt-2 text-crimson text-sm">{formErrors.password}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmNewPassword" className="block text-white font-medium mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFormErrors(prev => ({ ...prev, confirmPassword: null }));
                }}
                className={`w-full px-4 py-3 bg-white/10 border-2 ${
                  formErrors.confirmPassword ? 'border-crimson' : 'border-white/20'
                } text-white rounded-lg focus:border-gold focus:outline-none placeholder-neutral-grey-400`}
                placeholder="Re-enter new password"
              />
              {formErrors.confirmPassword && (
                <p className="mt-2 text-crimson text-sm">{formErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-gold hover:bg-gold-light text-dark-slate font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg shadow-gold/30"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="small" />
                  Resetting...
                </span>
              ) : 'Reset Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientResetPasswordPage;
